import type { ReactNode } from 'react'
import { CaptchaField } from '@/components/auth/captcha-field'
import { AccessRequestFields } from '@/components/access-request/access-request-fields'
import { useAccessRequestSubmit } from '@/components/access-request/use-access-request-submit'
import { interpolateCopy } from '@/lib/interpolate-copy'
import { cn } from '@/lib/utils'

type AccessRequestFormCoreProps = {
  variant: 'marketing' | 'auth' | 'marketingTheme'
  className?: string
  formClassName?: string
  successPanelClassName?: string
  errorClassName?: string
  marketingFieldClassName?: string
  marketingSelectTriggerClassName?: string
  marketingSelectContentClassName?: string
  marketingSelectItemClassName?: string
  renderSubmit: (props: { submitting: boolean; submitLabel: string }) => ReactNode
  formAriaLabel?: string
}

export function AccessRequestFormCore({
  variant,
  className,
  formClassName,
  successPanelClassName,
  errorClassName,
  marketingFieldClassName,
  marketingSelectTriggerClassName,
  marketingSelectContentClassName,
  marketingSelectItemClassName,
  renderSubmit,
  formAriaLabel,
}: AccessRequestFormCoreProps) {
  const {
    accessRequestConfig,
    submitted,
    submitting,
    error,
    email,
    setEmail,
    name,
    setName,
    role,
    setRole,
    company,
    setCompany,
    note,
    setNote,
    captchaRef,
    live,
    handleCaptchaTokenChange,
    handleSubmit,
    handleCaptchaMisconfigured,
    handleCaptchaExecuteError,
  } = useAccessRequestSubmit()

  const submitLabel = accessRequestConfig.submitLabel ?? 'Submit'
  const successTitle = accessRequestConfig.successTitle ?? 'Thank you'
  const successMessage = interpolateCopy(
    accessRequestConfig.successMessage ??
      "We've received your details and will be in touch soon.",
    { email },
  )

  if (submitted) {
    return (
      <div
        className={cn(successPanelClassName, className)}
        role="status"
        aria-live="polite"
      >
        <p className={variant === 'marketing' ? 'text-lg font-semibold text-white' : variant === 'marketingTheme' ? cn('text-lg font-semibold text-[var(--marketing-text)]') : 'text-lg font-semibold'}>
          {successTitle}
        </p>
        <p
          className={
            variant === 'marketing'
              ? 'mt-2 text-base leading-relaxed text-white/70'
              : variant === 'marketingTheme'
                ? 'mt-2 text-base leading-relaxed text-[var(--marketing-muted)]'
                : 'mt-2 text-sm text-muted-foreground'
          }
        >
          {successMessage}
        </p>
      </div>
    )
  }

  const fields = accessRequestConfig.fields ?? []

  return (
    <div className={className}>
      <form
        className={formClassName}
        onSubmit={handleSubmit}
        aria-label={formAriaLabel ?? submitLabel}
      >
        {error ? (
          <p className={errorClassName} role="alert">
            {error}
          </p>
        ) : null}
        <AccessRequestFields
          variant={variant}
          fields={fields}
          values={{ email, name, role, company, note }}
          setters={{ setEmail, setName, setRole, setCompany, setNote }}
          submitting={submitting}
          emailRequired={live}
          emailHint={accessRequestConfig.emailHint}
          emailLabel={accessRequestConfig.emailLabel}
          emailPlaceholder={accessRequestConfig.emailPlaceholder}
          className={
            variant === 'marketing'
              ? 'contents'
              : variant === 'marketingTheme'
                ? 'flex flex-col gap-5'
                : 'flex flex-col gap-6'
          }
          marketingFieldClassName={marketingFieldClassName}
          marketingSelectTriggerClassName={marketingSelectTriggerClassName}
          marketingSelectContentClassName={marketingSelectContentClassName}
          marketingSelectItemClassName={marketingSelectItemClassName}
        />
        {live ? (
          variant === 'marketing' ? (
            <div className="sm:col-span-2">
              <CaptchaField
                ref={captchaRef}
                action="waitlist"
                disabled={submitting}
                onTokenChange={handleCaptchaTokenChange}
                onMisconfigured={handleCaptchaMisconfigured}
                onExecuteError={handleCaptchaExecuteError}
              />
            </div>
          ) : (
            <CaptchaField
              ref={captchaRef}
              action="waitlist"
              variant={variant === 'marketingTheme' ? 'marketingTheme' : 'default'}
              disabled={submitting}
              onTokenChange={handleCaptchaTokenChange}
              onMisconfigured={handleCaptchaMisconfigured}
              onExecuteError={handleCaptchaExecuteError}
            />
          )
        ) : null}
        {renderSubmit({ submitting, submitLabel })}
      </form>
    </div>
  )
}
